import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Snackbar } from "@mui/material";
import { RiBookOpenLine, RiMailLine, RiMapPinLine, RiNotification3Line, RiPhoneLine, RiStickyNoteLine, RiTicket2Line } from "@remixicon/react";
import { countTutorBookings, getUserById, PHONE_ALIASES } from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { studentMenuItems, tabFromPath } from "../../utils/studentDetail";
import { TUTOR_TABS, tutorChips, tutorMeta, tutorTabCount } from "../../utils/tutorDetail";
import { subjectList } from "../../utils/tutorPresentation";
import { tokens } from "../../utils/figmaTokens";
import { useBreadcrumbName } from "../../components/Layout/BreadcrumbContext";
import { InfoItem, InfoRow, PageHead } from "../../components/kit/PageHead";
import LinkTabs from "../../components/kit/LinkTabs";
import AccountMenu from "../../components/kit/AccountMenu";
import AdjustBalanceDialog from "../../components/AdjustBalanceDialog/AdjustBalanceDialog";
import BanAccountDialog from "../../components/BanAccountDialog/BanAccountDialog";
import AddNoteDialog from "../../components/StaffNotes/AddNoteDialog";
import { useNotesCount } from "../../components/StaffNotes/NotesTab";
import { OpenTicketDialog } from "../Lessons/LessonActions";
import SendNotificationDialog from "../Students/SendNotificationDialog";
import EditTutorDialog from "./EditTutorDialog";

const phoneOf = (u) => PHONE_ALIASES.map((f) => u?.[f]).find(Boolean) || "";

// The tutor page (047 D43–D52), drawn from Figma's Teacher overview (10967:48518) the way the
// student page is (D13, D14): a white card holding the head, the tabs and the open tab. The tab
// comes from the URL and each tab loads alone through the outlet. An id that is not a tutor's is
// "not found" here. The head's buttons and the ⋯ menu are the student page's, each behind its
// permission; Edit needs EDIT_TUTORS (D46).
const TutorPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const userRole = user?.role || "";
  const location = useLocation();
  const navigate = useNavigate();

  const [tutor, setTutor] = useState(null);
  const [missing, setMissing] = useState(false);
  const [sessionsTotal, setSessionsTotal] = useState(null);
  const [dialog, setDialog] = useState(""); // "" | "edit" | "adjust" | "notify" | "ticket" | "disable" | "note"
  const [snack, setSnack] = useState(null); // { message, severity }
  // The Notes pill (059): recounted after every add or delete, here or in the tab.
  const notes = useNotesCount(id);

  const notify = useCallback((message, severity = "success") => setSnack({ message, severity }), []);

  // The top bar's "Tutors / name" (047 D11); "Not found" once the id is (D83).
  useBreadcrumbName(id, tutor ? tutor.name || "Unnamed tutor" : missing ? "Not found" : null);

  // As on the student page: a refresh that fails keeps what is on screen, only a first load can
  // end in "Tutor not found.", and the newest tutor wins.
  const latest = useRef("");
  const load = useCallback(async () => {
    latest.current = id;
    const found = await getUserById(id);
    if (latest.current !== id) return;
    const isTutor = found?.role === "teacher";
    if (isTutor) setTutor(found);
    setMissing(!isTutor);
  }, [id]);

  useEffect(() => {
    setTutor(null);
    setMissing(false);
    load();
  }, [load]);

  // The Sessions pill and the Overview's total: one count per tutor (D48). No pill until it lands.
  useEffect(() => {
    let cancelled = false;
    setSessionsTotal(null);
    countTutorBookings(id)
      .then((n) => !cancelled && setSessionsTotal(n))
      .catch((e) => console.error("Error counting the tutor's sessions:", e));
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!tutor) {
    return missing ? (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          Tutor not found.
        </Alert>
        <Button component={RouterLink} to="/tutors" sx={{ mt: 2 }}>Back to Tutors</Button>
      </Box>
    ) : (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const phone = phoneOf(tutor);
  // 047 D75: every subject, in the head.
  const subjects = subjectList(tutor.subjects).join(", ");
  // The merged user (the student page's rule, 047 D64): the server checks the same.
  const can = (p) => hasPermission(user, p);
  const menuItems = studentMenuItems(can, tutor, permissions.EDIT_TUTORS);
  const tab = tabFromPath(location.pathname, id, { base: "/tutors", tabs: TUTOR_TABS });
  const closeDialog = () => setDialog("");
  const afterWrite = (message) => {
    closeDialog();
    load();
    if (message) notify(message);
  };

  return (
    <Box
      sx={{
        bgcolor: tokens.surface.default,
        border: `1px solid ${tokens.border.default}`,
        borderRadius: "16px",
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 3, md: 4 },
      }}
    >
      <PageHead
        name={tutor.name || "Unnamed tutor"}
        colorKey={tutor.id}
        chips={tutorChips(tutor)}
        lines={
          (tutor.email || phone || tutor.timeZone || subjects) && (
            <InfoRow>
              {tutor.email && <InfoItem Icon={RiMailLine}>{tutor.email}</InfoItem>}
              {phone && <InfoItem Icon={RiPhoneLine}>{phone}</InfoItem>}
              {/* Figma's location slot: no location is stored, the time zone is. */}
              {tutor.timeZone && <InfoItem Icon={RiMapPinLine}>{tutor.timeZone}</InfoItem>}
              {subjects && <InfoItem Icon={RiBookOpenLine}>{subjects}</InfoItem>}
            </InfoRow>
          )
        }
        meta={tutorMeta(tutor)}
        actions={
          <>
            {can(permissions.EDIT_LESSONS) && (
              <Button size="large" variant="outlined" startIcon={<RiTicket2Line size={20} />} onClick={() => setDialog("ticket")}>
                Open ticket
              </Button>
            )}
            <Button size="large" variant="outlined" startIcon={<RiStickyNoteLine size={20} />} onClick={() => setDialog("note")}>
              Add note
            </Button>
            {can(permissions.SEND_NOTIFICATIONS) && (
              <Button size="large" variant="contained" startIcon={<RiNotification3Line size={20} />} onClick={() => setDialog("notify")}>
                Send notification
              </Button>
            )}
            <AccountMenu items={menuItems} disabled={tutor.disabled} onPick={setDialog} />
          </>
        }
      />

      <LinkTabs
        label="Tutor sections"
        value={tab}
        tabs={TUTOR_TABS.map((t) => ({
          value: t.path,
          label: t.label,
          to: t.path ? `/tutors/${id}/${t.path}` : `/tutors/${id}`,
          count: t.key === "notes" ? notes.count : tutorTabCount(t.key, tutor, sessionsTotal),
        }))}
      />

      {/* Keyed on the tutor: each tutor mounts fresh tabs. `openDialog` lets a tab open the page's dialogs. */}
      <Outlet
        key={tutor.id}
        context={{
          tutor,
          refreshTutor: load,
          actingUser: user,
          userRole,
          notify,
          sessionsTotal,
          openDialog: setDialog,
          notesVersion: notes.version,
          onNotesChanged: notes.bump,
        }}
      />

      <AddNoteDialog
        open={dialog === "note"}
        onClose={closeDialog}
        personUid={tutor.id}
        actingUser={user}
        onSaved={(message) => {
          notes.bump();
          notify(message);
        }}
      />
      <EditTutorDialog open={dialog === "edit"} onClose={closeDialog} tutor={tutor} actingUser={user} onSaved={() => afterWrite("Saved.")} />
      <AdjustBalanceDialog open={dialog === "adjust"} onClose={closeDialog} person={tutor} actingUser={user} onAdjusted={() => afterWrite()} />
      <BanAccountDialog open={dialog === "disable"} onClose={closeDialog} person={tutor} actingUser={user} onDone={() => afterWrite()} />
      <SendNotificationDialog
        open={dialog === "notify"}
        onClose={closeDialog}
        person={tutor}
        noun="tutor"
        actingUser={user}
        onSent={(message) => notify(message)}
      />
      <OpenTicketDialog
        open={dialog === "ticket"}
        onClose={closeDialog}
        booking={null}
        userId={tutor.id}
        label=""
        personName={tutor.name || "this tutor"}
        actingUser={user}
        onDone={(message, ticketId) => {
          notify(message);
          if (ticketId) navigate(`/inbox/${ticketId}`);
        }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity={snack?.severity || "success"} onClose={() => setSnack(null)}>{snack?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TutorPage;
